import React, { useContext, useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { DateTime } from 'luxon';
import { Redirect, useHistory } from 'react-router-dom';
import { Card, DateRangeFilter, InfoTray, FilterCriteriaRow, FloatingInput } from 'src/components';
import { HeaderNav } from 'src/containers';
import { MerchantProfileContext } from 'src/context/merchant-profile-context';
import { TransactionFilterProvider } from 'src/context/transaction-filter-context';
import { getTwelveAMOfToday } from 'src/helpers/dates';
import { Environment } from 'src/Environment';
import {
  staticDashboard,
  fundMyNextReadMoreUrl,
  fundMyNextUrl,
} from 'src/services/payment-dashboard.service';
import { MiniAppFocusWrapper } from 'src/components/miniapp-focus-wrapper/miniapp-focus-wrapper';
import { getBCAMerchantAccountBalance } from 'src/services/merchant-account.service';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import TealiumEvents from 'src/helpers/tealium-events';
import { OfferContext } from 'src/context/offer-context';
import {
  businessCashAdvancePreApprovedRoute,
  businessCashAdvanceTopUpCalculationRoute,
} from 'src/routing/routes';
import FilteredDashboard from './filtered-dashboard/filtered-dashboard';
import MiniAppEvents from 'src/helpers/miniapp-events';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import TechnicalErrorSelfService from '../technical-error-self-service/technical-error-self-service';
import { queue } from 'src/helpers/delay';
import './payment-services-dashboard.scss';

const QualificationStatus = {
  HAS_OFFER: 1,
  NO_OFFER: 2,
};

function PaymentServicesDashboard() {
  const inMiniProgram = MiniAppEvents.isInMiniApp();
  const { merchant } = useContext(MerchantProfileContext);
  const { offers, isBCATopUp } = useContext(OfferContext);
  const today = getTwelveAMOfToday();
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  const allTerminalsOption = { type: 'All terminals', id: null };

  const selectedDates = useState([weekAgo, today]);
  const [selectedTerminalType, setSelectedTerminalType] = useState(null);
  const [terminalFilterOptions, setTerminalFilterOptions] = useState([]);
  const [search, setSearch] = useState(false);

  const [recentTransactions, setRecentTransactions] = useState(null);
  const [accounts, setAcccounts] = useState(null);

  const [redirect, setRedirect] = useState(null);

  const history = useHistory();

  const [recentTransactionsFailure, setRecentTransactionsFailure] = useState(false);
  const [accountBalanceFailure, setAccountBalanceFailure] = useState(false);
  const [filteredDashboardFailure, setFilteredDashboardFailure] = useState(false);
  const [recentTractionData, setrecentTractionData] = useState({});

  const dateRangeFilter = () => {
    return <DateRangeFilter tealiumEvent={TealiumEvents.dashboardDateFilterApplied} />;
  };

  const terminalSelectFilter = () => {
    return (
      <div className="select-filter has-margin-bottom-6">
        <Field
          name="terminal"
          type="text"
          placeholder="Terminal"
          component={MiniAppFocusWrapper}
          innerComponent={FloatingInput}
          dontClearAutocompleteOnClick={true}
          items={[allTerminalsOption, ...terminalFilterOptions]}
          getItemDisplayValue={(item) => item.type}
          filterItemsWithInputValue={true}
          onItemSelect={(item) => {
            TealiumEvents.dashboardTerminalFiltersApplied(item.type);

            const selected = terminalFilterOptions.find((terminal) => {
              return terminal.id.toString() === item.id;
            });

            if (selected) {
              setSelectedTerminalType(selected);
              return;
            }

            setSelectedTerminalType(null);
          }}
          fieldType="select"
          isDark={true}
          hasNoAfterLine={true}
        />
      </div>
    );
  };

  const checkTopUpOfferStatus = () => {
    if (
      !Environment.current.disabledFeatures.bcaTopUp &&
      offers?.offerDetails?.QualificationStatus === QualificationStatus.HAS_OFFER &&
      offers.offerDetails.CurrentBalance > 0 &&
      window.innerWidth > 769
    ) {
      toastMessageServiceSingleton.addToast({
        message: (
          <>
            You qualify for a Business Cash Advance Top-up of{' '}
            <span className="has-text-weight-bold is-nowrap">
              R {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
            </span>
          </>
        ),
        actionButtonText: 'View offer',
        actionButtonOnClick: () => {
          history.push(businessCashAdvanceTopUpCalculationRoute);
        },
        permanent: true,
        className: 'hidden md:block',
      });
    }
  };

  const checkOfferStatus = () => {
    if (
      offers?.offerDetails?.QualificationStatus === QualificationStatus.HAS_OFFER &&
      offers.offerDetails.CurrentBalance <= 0 &&
      window.innerWidth > 769
    ) {
      TealiumEvents.dashboardBCAQualifiesToastAppears();

      toastMessageServiceSingleton.addToast({
        message: (
          <>
            Business Cash Advance available:{' '}
            <span className="has-text-weight-bold is-nowrap">
              R {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
            </span>
          </>
        ),
        actionButtonText: 'Apply now',
        actionButtonOnClick: () => {
          TealiumEvents.dashboardApplyNowClicked(businessCashAdvancePreApprovedRoute);

          history.push(businessCashAdvancePreApprovedRoute);
        },
        permanent: true,
      });
    }
  };

  useEffect(() => {
    if (merchant.merchantId) {
      TealiumEvents.dashboardPageLoadedNewStyle();
    }
  }, [merchant]);

  useEffect(() => {
    checkOfferStatus();
    checkTopUpOfferStatus();
  }, [offers?.offerDetails?.QualificationStatus]);

  useEffect(() => {
    const abortController = new AbortController();

    const generateTable = (recentTransactions) => {
      const transactionRows = [];
      for (let x = 0; x < recentTransactions.length && x < 6; x++) {
        const transaction = (
          <tr key={`transactions-${x}`}>
            <td className="table-data-icon is-narrow">
              <div className="icon-circle-container">
                <span className="item-icon-red is-icon-4 icon-bill"></span>
              </div>
            </td>
            <td automationid={`PaymentDashboard_TD_RecentTransactionAmount_${x}`}>
              <span className="table-data-sub-text">
                {DateTime.fromISO(recentTransactions[x].transaction.processingDate).toFormat(
                  'dd MMM yyyy | HH:mm',
                )}
              </span>
              <br />
              <span className="table-data-description">
                {recentTransactions[x].transaction.transactionTypeDescription}
              </span>
              <br />
              <span className="table-data-sub-text">
                {recentTransactions[x].transaction.methodOfPayment}
              </span>
              <br />
            </td>
            <td
              className="table-data-price text-top-right"
              automationid={`PaymentDashboard_TD_RecentTransactionMethodOfPayment_${x}`}
            >
              R {formatCurrencyAmountSimple(recentTransactions[x].transaction.transactionAmount)}
            </td>
          </tr>
        );
        transactionRows.push(transaction);
      }
      return transactionRows.length > 0 ? transactionRows : null;
    };

    const getRecentTransaction = async () => {
      const data = await staticDashboard(
        abortController,
        () => setRecentTransactionsFailure(true),
        false,
        true,
      );
      setrecentTractionData(data);
    };

    const getStaticDashboard = async () => {
      const accountBalance = await getBCAMerchantAccountBalance(
        abortController,
        () => setAccountBalanceFailure(true),
        false,
        true,
      );

      if (!abortController.signal.aborted && recentTractionData && accountBalance.length > 0) {
        setRecentTransactions(generateTable(recentTractionData.transactionsArray || []));
        setAcccounts([
          {
            description: 'Account balance',
            value: accountBalance[0].currentBalance,
            subtext: 'Amount to be settled',
          },
          {
            description: 'Last settlement',
            value: recentTractionData.settlementDue,
            subtext: DateTime.fromISO(recentTractionData.settlementDate).toFormat('dd/MM/yyyy'),
          },
        ]);
      } else if (!abortController.signal.aborted && recentTractionData) {
        setRecentTransactions(generateTable(recentTractionData.transactionsArray || []));
        setAcccounts([
          {
            description: 'Last settlement',
            value: recentTractionData.settlementDue,
            subtext: DateTime.fromISO(recentTractionData.settlementDate).toFormat('dd/MM/yyyy'),
          },
        ]);
      }
    };

    queue.push(getRecentTransaction);
    queue.push(getStaticDashboard);

    return () => {
      abortController.abort();
    };
  }, []);

  useEffect(() => {
    if (recentTransactionsFailure && filteredDashboardFailure && accountBalanceFailure)
      throw new Error(
        'Payment Services Dashboard: error response from the recent transactions, filtered dashboards and account balance GET endpoints',
      );
  }, [recentTransactionsFailure, filteredDashboardFailure, accountBalanceFailure]);

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  return (
    <div className="PaymentServicesDashboard" data-testid="PaymentServicesDashboard">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
        screenName="Payment Services"
      >
        <TransactionFilterProvider value={selectedDates}>
          <p className="filter-title is-vcentered">
            <span className="is-hidden-mobile">Filter by</span>
            <span className="is-hidden-tablet">Filter by:</span>

            <span
              className="is-hidden-tablet item-icon-white is-clickable is-icon-4 icon-filters is-pulled-right"
              onClick={() => setSearch(true)}
            ></span>
          </p>

          <Formik
            initialValues={{ terminal: allTerminalsOption.type }}
            validateOnChange
            validate={() => setSearch(false)}
          >
            {({ setValues }) => {
              return (
                <>
                  <Form className="columns is-multiline filter-form is-hidden-mobile">
                    <div className="column is-narrow select-filter has-margin-bottom-6 has-margin-right-6">
                      {dateRangeFilter()}
                    </div>
                    <div className="column is-narrow select-filter has-margin-bottom-6">
                      {terminalSelectFilter()}
                    </div>
                  </Form>

                  <FilterCriteriaRow
                    className="is-hidden-tablet"
                    showDate={true}
                    filterCriteria={[
                      {
                        value: selectedTerminalType?.type,
                        clear: () => {
                          setSelectedTerminalType(null);
                          setValues({ terminal: allTerminalsOption.type });
                        },
                      },
                    ]}
                  />

                  <InfoTray selected={search} setSelected={setSearch} title="Filter by">
                    <Form>
                      <FilterCriteriaRow
                        showDate={true}
                        filterCriteria={[
                          {
                            value: selectedTerminalType?.type,
                            clear: () => {
                              setSelectedTerminalType(null);
                              setValues({ terminal: allTerminalsOption.type });
                            },
                          },
                        ]}
                      />
                      <div className="has-margin-bottom-6">{dateRangeFilter()}</div>
                      {terminalSelectFilter()}
                    </Form>
                  </InfoTray>
                </>
              );
            }}
          </Formik>
          {offers?.offerDetails?.QualificationStatus === 1 && !isBCATopUp && (
            <div className="is-hidden-tablet mobile-qualified-offer-card">
              <span>
                Business cash advance avaliable:{' '}
                <span className="has-text-weight-bold is-nowrap">
                  R {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
                </span>
              </span>
              <a
                className="is-pulled-right"
                onClick={() => {
                  TealiumEvents.dashboardApplyNowClicked(businessCashAdvancePreApprovedRoute);
                  setRedirect(businessCashAdvancePreApprovedRoute);
                }}
                target="_blank"
              >
                Apply
              </a>
            </div>
          )}

          {!Environment.current?.disabledFeatures.bcaTopUp &&
            offers?.offerDetails?.QualificationStatus === 1 &&
            isBCATopUp && (
              <div className="is-hidden-tablet mobile-qualified-offer-card">
                <span>
                  You qualify for a Business Cash Advance Top-up of{' '}
                  <span className="has-text-weight-bold is-nowrap">
                    R {formatCurrencyAmountSimple(offers?.offerDetails?.AvailableLimit)}
                  </span>
                </span>
                <a
                  className="is-pulled-right"
                  onClick={() => {
                    TealiumEvents.dashboardApplyNowClicked(businessCashAdvancePreApprovedRoute);
                    history.push(businessCashAdvanceTopUpCalculationRoute);
                  }}
                  target="_blank"
                >
                  Apply
                </a>
              </div>
            )}

          <FilteredDashboard
            filterByTerminal={selectedTerminalType}
            setTerminalOption={(options) => {
              setTerminalFilterOptions(options);
            }}
            onFailure={() => setFilteredDashboardFailure(true)}
          ></FilteredDashboard>
          <div className="is-multiline columns">
            <Card className="column recent-transactions-card" cardTitle="Recent transactions">
              {recentTransactionsFailure ? (
                <TechnicalErrorSelfService manualFailure="transactions" hasError />
              ) : recentTransactions ? (
                <table className="table is-fullwidth">
                  <tbody>{recentTransactions}</tbody>
                </table>
              ) : (
                <div automationid={`PaymentDashboard_Div_RecentTransactionNoData`}>
                  No transactions in past 30 days
                </div>
              )}
            </Card>
            <div className="column accounts-fundme-column">
              <Card cardTitle="Accounts" className="accounts-card">
                {accountBalanceFailure ? (
                  <TechnicalErrorSelfService manualFailure="account-balance" hasError />
                ) : accounts ? (
                  <table className="table accounts-table is-fullwidth">
                    <tbody>
                      {accounts.map((account, index) =>
                        account.value ? (
                          <tr key={`account-${index}`}>
                            <td>
                              <p className="table-data-description">{account.description}</p>
                              <p className="has-text-weight-light is-size-7 subtext">
                                {account.subtext}
                              </p>
                            </td>
                            <td
                              automationid={`PaymentDashboard_TD_SettlementAmount`}
                              className="text-right table-data-price"
                            >
                              R {formatCurrencyAmountSimple(account.value)}
                            </td>
                          </tr>
                        ) : (
                          <tr key={`account-no-data-${index}`}>
                            <td automationid={`PaymentDashboard_Div_SettlementDueNoData`}>
                              No data
                            </td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                ) : (
                  <div automationid={`PaymentDashboard_Div_SettlementNoData`}>
                    No data to display
                  </div>
                )}
              </Card>

              <Card className={!inMiniProgram ? 'offer-card' : 'card-miniapp'}>
                <div className="columns is-multiline is-mobile">
                  <div className="column is-one-third image-container"></div>
                  <div className="column is-two-thirds details-container">
                    <div className="text-container">
                      <p className="offer-title">Fund my next move</p>
                      <br />
                      <p>
                        Apply in minutes and receive funding in hours. No paperwork, no hidden fees
                        and no branch visits.{' '}
                        {!inMiniProgram && (
                          <a
                            className="is-red-text-link"
                            href={fundMyNextReadMoreUrl}
                            target="_blank"
                            automationid="Payment_Services_Dashboard_Link_Fund_My_Next_Move_More_Info"
                            rel="noreferrer"
                          >
                            More info...
                          </a>
                        )}
                      </p>
                    </div>
                    <div className="button-container">
                      {!inMiniProgram && (
                        <a
                          className="view-more"
                          href={fundMyNextUrl}
                          onClick={() =>
                            TealiumEvents.dashboardApplyNowClicked(fundMyNextReadMoreUrl)
                          }
                          target="_blank"
                          automationid="Payment_Services_Dashboard_Link_Fund_My_Next_Move_Apply_Now"
                          rel="noreferrer"
                        >
                          Apply now
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </TransactionFilterProvider>
      </HeaderNav>
    </div>
  );
}

export default PaymentServicesDashboard;
